/**
 * +world-resources: Venom Altar breakables, hidden caches and reagent nodes
 * (docs/design/world-resources.md §3; art bible §5 clutter lists, §6.1 clear radii).
 * Per-room kinds follow the room's dressing: urns/crates/bones at the gate, eggs in the cloister,
 * eggs, cocoons and crates in the mine hatchery, bowls and urns in the treasury, bones in the pit.
 */
import type { DungeonDef, WorldObjectDef } from '@keystone/sim';
import { type BossClear, type RoomWorldSpec, packCentre, placeWorldObjects } from '../world/placement';

/** Boss arenas: clear radius around the boss (art bible §6.1) on top of the central 70%. */
const BOSS_RADIUS: Record<string, number> = { ravager_pit: 20, coil_cistern: 18, fang_altar: 20 };

export const VENOM_ALTAR_WORLD: RoomWorldSpec[] = [
  { roomId: 'gate', breakables: 9, middle: 0.3, kinds: { urn: 3, crate: 2, bone_pile: 1 } },
  { roomId: 'cloister', breakables: 12, middle: 0.35, kinds: { egg_clutch: 2, urn: 2, cocoon: 1, bone_pile: 1 }, caches: 1 },
  { roomId: 'ravager_pit', breakables: 6, middle: 0, kinds: { bone_pile: 3, crate: 1 } },
  { roomId: 'scriptorium', breakables: 11, middle: 0.35, kinds: { crate: 2, urn: 2, offering_bowl: 2 }, caches: 1 },
  {
    roomId: 'hatchery',
    breakables: 9,
    middle: 0.35,
    kinds: { egg_clutch: 3, cocoon: 2, crate: 2 },
    caches: 1,
    nodes: [{ kind: 'venom_gland', near: { x: 168, y: 216 } }],
  },
  { roomId: 'coil_cistern', breakables: 5, middle: 0, kinds: { urn: 2, offering_bowl: 1, cocoon: 1 }, nodes: [{ kind: 'eye_shard', near: { x: 248, y: 236 } }] },
  {
    roomId: 'venom_crypt',
    breakables: 11,
    middle: 0.35,
    kinds: { urn: 2, cocoon: 2, bone_pile: 1 },
    caches: 2,
    nodes: [
      { kind: 'venom_gland', near: { x: 198, y: 272 } },
      { kind: 'venom_gland', near: { x: 254, y: 264 } },
    ],
  },
  { roomId: 'treasury', breakables: 8, middle: 0.35, kinds: { offering_bowl: 3, urn: 2 }, caches: 1, nodes: [{ kind: 'eye_shard', near: { x: 310, y: 298 } }] },
  { roomId: 'fang_stair', breakables: 8, middle: 0.35, kinds: { crate: 2, offering_bowl: 1, bone_pile: 1 } },
  { roomId: 'fang_altar', breakables: 5, middle: 0, kinds: { offering_bowl: 2, urn: 1 } },
];

export function venomAltarBossClears(d: DungeonDef): BossClear[] {
  return d.packs.filter((p) => p.encounterId).map((p) => ({ roomId: p.roomId, center: packCentre(p), radius: BOSS_RADIUS[p.roomId] ?? 18 }));
}

export function venomAltarWorldObjects(d: DungeonDef): WorldObjectDef[] {
  return placeWorldObjects(d, VENOM_ALTAR_WORLD, venomAltarBossClears(d), 0x5eed);
}
